import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

export default function useLineageTransformationData({
  assetName,
  preferredMermaidName,
}) {
  const [loading, setLoading] = useState(false);
  const [mermaidDiagrams, setMermaidDiagrams] = useState([]);
  const [selectedMermaid, setSelectedMermaid] = useState(null);
  const selectionTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
      selectionTimeoutRef.current = null;
    }

    if (!assetName) {
      setMermaidDiagrams([]);
      setSelectedMermaid(null);
      setLoading(false);
      return;
    }

    let isActive = true;

    const fetchLineageTransformation = async () => {
      try {
        setLoading(true);
        const apiEndpoint = `${process.env.REACT_APP_API_URL}/lineage-transformation?asset_name=${encodeURIComponent(assetName)}`;
        const response = await axios.get(apiEndpoint);

        if (!isActive) return;

        setMermaidDiagrams(response?.data?.mermaid_diagrams || []);
      } catch (err) {
        if (!isActive) return;
        setMermaidDiagrams([]);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchLineageTransformation();

    return () => {
      isActive = false;
    };
  }, [assetName]);

  useEffect(() => {
    if (!mermaidDiagrams.length) {
      setSelectedMermaid(null);
      return;
    }

    const nextMermaid =
      mermaidDiagrams.find((diagram) => diagram.name === preferredMermaidName) ||
      mermaidDiagrams[0];

    setSelectedMermaid(nextMermaid);
  }, [mermaidDiagrams, preferredMermaidName]);

  const handleSelectMermaid = useCallback(
    (mermaidName) => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }

      setLoading(true);
      selectionTimeoutRef.current = setTimeout(() => {
        setSelectedMermaid(
          mermaidDiagrams.find((diagram) => diagram.name === mermaidName) ||
            mermaidDiagrams[0] ||
            null
        );
        setLoading(false);
      }, 500);
    },
    [mermaidDiagrams]
  );

  const mermaidTypes = useMemo(
    () => mermaidDiagrams.map((diagram) => diagram.name),
    [mermaidDiagrams]
  );

  return {
    handleSelectMermaid,
    loading,
    mermaidTypes,
    selectedMermaid,
  };
}